import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {FC, useState, useEffect, useMemo} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from 'react-bootstrap'
import {getOptionAgreements, getPendingAgreement, submitAgreement} from './core/_requests'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {AgreementDetail, AgreementSource} from './core/_models'
import './TradeDataAgreementReviewPage.scss'
import {MultiValue} from 'react-select'
import ModalAddSources from '../trade-data-agreement-detail/components/ModalAddSources'
import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import AgreementOriginForm from './components/AgreementOriginForm'
import AgreementPendingForm from './components/AgreementPendingForm'
import AgreementComparisonForm from './components/AgreementComparisonForm'

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

const TradeDataAgreementReviewPage: FC = () => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {idAgreement} = useParams()
  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [agreementStatus, setAgreementStatus] = useState([])
  const [agreementType, setAgreementType] = useState([])
  const [agreementNotification, setAgreementNotification] = useState([])
  const [agreementComposition, setAgreementComposition] = useState([])
  const [countries, setCountries] = useState<any[]>([])
  const [agreementPending, setAgreementPending] = useState<AgreementDetail>({
    acronym: '',
    agreement: '',
    type: [],
    wto_rta_id: '',
    status: '',
    notification: [],
    date_signed: '',
    date_into_force: '',
    date_notification: '',
    date_of_implementation: '',
    end_implementation: '',
    date_inactive: '',
    composition: '',
    region: '',
    parties_wto: '',
    crossregional: '',
    language: '',
    sources: '',
    date_xml_created: '',
    date_xml_edited: '',
    xml_file: '',
    xml_edited_comments: '',
    text_available: '',
    document_type: null,
    parties_escap: '',
    services_schedules: '',
    wits_id: '',
    desta_id: '',
    tota_id: '',
    aptiad_id: '',
    iso: [], //refer to parties
  })
  const [agreementOrigin, setAgreementOrigin] = useState<AgreementDetail>({
    acronym: '',
    agreement: '',
    type: [],
    wto_rta_id: '',
    status: '',
    notification: [],
    date_signed: '',
    date_into_force: '',
    date_notification: '',
    date_of_implementation: '',
    end_implementation: '',
    date_inactive: '',
    composition: '',
    region: '',
    parties_wto: '',
    crossregional: '',
    language: '',
    sources: '',
    date_xml_created: '',
    date_xml_edited: '',
    xml_file: '',
    xml_edited_comments: '',
    text_available: '',
    document_type: null,
    parties_escap: '',
    services_schedules: '',
    wits_id: '',
    desta_id: '',
    tota_id: '',
    aptiad_id: '',
    iso: [], //refer to parties
  })

  const isDifferent = (originVal: any, newVal: any) => {
    if (!originVal && !newVal) return false

    if (Array.isArray(originVal) || Array.isArray(newVal)) {
      return JSON.stringify(originVal || []) !== JSON.stringify(newVal || [])
    }
    return String(originVal || '').trim() !== String(newVal || '').trim()
  }

  // sources
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [originalSources, setOriginalSources] = useState<AgreementSource[]>([])
  const [pendingSources, setPendingSources] = useState<AgreementSource[]>([])
  const [sourceAlias, setSourceAlias] = useState('')
  const [sourceLink, setSourceLink] = useState('')

  const sourceColumnHelper = createColumnHelper<AgreementSource>()

  const handleAddSource = () => {
    if (!sourceAlias.trim() || !sourceLink.trim()) return

    setPendingSources((prev) => [
      ...prev,
      {
        id: `${Date.now()}`, // simple unique id
        alias: sourceAlias.trim(),
        link: sourceLink.trim(),
      },
    ])

    // reset input
    setSourceAlias('')
    setSourceLink('')
  }

  const handleDeleteSource = (id: string) => {
    setPendingSources((prev) => prev.filter((item) => item.id !== id))
  }

  const sourceColumns: ColumnDef<AgreementSource, any>[] = useMemo(
    () => [
      sourceColumnHelper.accessor('alias', {
        header: 'Alias',
        cell: (info) => info.getValue() || '-',
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left fw-semibold',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('link', {
        header: 'Link',
        cell: (info) => {
          const value = info.getValue()
          return value ? (
            <a href={value} target='_blank' rel='noreferrer' className='text-link'>
              {value}&nbsp;
              {info.row.original.is_source_valid !== undefined && (
                <i
                  className={`fa-solid ${
                    info.row.original.is_source_valid === '1'
                      ? 'fa-check text-success'
                      : 'fa-xmark text-danger'
                  }`}
                ></i>
              )}
            </a>
          ) : (
            '-'
          )
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left text-break',
        },
        enableSorting: false,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('id', {
        header: '',
        cell: (info) => {
          const id = info.getValue()

          return (
            <Link to='#' className='link-danger' onClick={() => handleDeleteSource(id)}>
              <span className='fa fa-trash'></span>
            </Link>
          )
        },
        meta: {
          headerClass: 'text-end',
          cellClass: 'text-end',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [originalSources]
  )

  const parseSources = (rawSources?: string, rawSourceValidity?: string): AgreementSource[] => {
    if (!rawSources) return []

    const validityArray = rawSourceValidity ? rawSourceValidity.split(';') : []

    // legacy case (single link, no |)
    if (!rawSources.includes('|')) {
      return [
        {
          id: `${Date.now()}_${Math.random()}`,
          alias: rawSources,
          link: rawSources,
          is_source_valid: validityArray[0] || '1',
        },
      ]
    }

    return rawSources.split(';').map((pair, index) => {
      const [alias, link] = pair.split('|')

      return {
        id: `${Date.now()}_${index}`,
        alias: alias || link,
        link: link,
        is_source_valid: validityArray[index] || '1',
      }
    })
  }

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      const optionsRes = await getOptionAgreements(axiosPrivate)
      const resData = optionsRes.data.data
      setAgreementStatus(resData.status)
      setAgreementType(resData.type.map((n: string) => ({value: n, label: n})))
      setAgreementNotification(resData.notification.map((n: string) => ({value: n, label: n})))
      setAgreementComposition(resData.composition)
      setCountries([
        {value: 'EUN', label: 'European Union (EUN)', region_un: ['Europe'], escap: false},
        {value: 'TWN', label: 'Other Asia (TWN)', region_un: ['Asia Pacific'], escap: false},
        ...resData.parties, // ← backend must return this
      ])

      const detailRes = await getPendingAgreement(axiosPrivate, {idAgreement})
      const detailResdata = detailRes.data.data
      if (detailResdata && detailResdata.agreement_pending_review) {
        const pending = detailResdata.agreement_pending_review
        const origin = detailResdata.agreement_origin

        const processData = (data: any) => {
          if (!data) return null
          return {
            ...data,
            type: data.type ? data.type.split(';') : [],
            notification: data.notification ? data.notification.split(';') : [],
            iso: data.iso ? data.iso.split(';') : [],
            sources: parseSources(data.sources, data.is_sources_valid),
          }
        }

        const pendingProcessed = processData(pending)
        setAgreementPending((prev) => ({...prev, ...pendingProcessed}))
        setPendingSources(pendingProcessed.sources || [])

        if (origin) {
          const originProcessed = processData(origin)
          setOriginalSources(originProcessed.sources || [])
          setAgreementOrigin((prev) => ({...prev, ...originProcessed}))
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAgreement(false)
    }
  }

  useEffect(() => {
    fetchOptionAgreement()
  }, [])

  // Add or Edit
  const handleAgreementChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value, multiple, selectedOptions} = e.target as HTMLSelectElement

    if (multiple) {
      const values = Array.from(selectedOptions, (option) => option.value)
      setAgreementPending((prev) => ({
        ...prev,
        [name]: values,
      }))
    } else {
      setAgreementPending((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handlePartiesChange = (selected: MultiValue<any>) => {
    const selectedValues = selected?.map((s) => s.value) || []

    // 🔥 If nothing selected → reset derived fields
    if (selectedValues.length === 0) {
      setAgreementPending((prev) => ({
        ...prev,
        iso: [],
        region: '',
        crossregional: '',
        parties_escap: '',
      }))
      return
    }

    const selectedCountries = countries.filter((c) => selectedValues.includes(c.value))

    const regionSet = new Set<string>()
    selectedCountries.forEach((c) => {
      c.region_un?.forEach((r: string) => regionSet.add(r))
    })

    const regionArray = Array.from(regionSet)

    const crossregional = regionArray.length > 1 ? 'Yes' : 'No'

    const partiesEscap = selectedCountries.some((c) => c.escap) ? 'Yes' : 'No'

    setAgreementPending((prev) => ({
      ...prev,
      iso: selectedValues,
      region: regionArray.join('; '),
      crossregional,
      parties_escap: partiesEscap,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = [
      'agreement',
      'acronym',
      'type',
      'status',
      'region',
      'parties_wto',
      'composition',
      'crossregional',
      'wto_rta_id',
      'wits_id',
      'tota_id',
      'aptiad_id',
    ]

    const isValid = requiredFields.every((key) => {
      const val = (agreementPending as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateRequiredFields()
  }, [agreementPending])

  const serializeSources = (sources: AgreementSource[], legacySource?: any) => {
    // CASE 1: new multi-source UI
    if (sources.length > 0) {
      return sources
        .filter((s) => s.link)
        .map((s) => `${s.alias || s.link}|${s.link}`)
        .join(';')
    }

    // CASE 2: legacy string → upgrade format
    if (typeof legacySource === 'string' && legacySource.trim() !== '') {
      const link = legacySource.trim()
      return `${link}|${link}`
    }

    // CASE 3: empty
    return ''
  }

  const serializeSourcesValidity = (sources: AgreementSource[], legacySource?: any) => {
    if (sources.length > 0) {
      return sources
        .filter((s) => s.link)
        .map((s) => String(s.is_source_valid) ?? '0')
        .join(';')
    }

    if (typeof legacySource === 'string' && legacySource.trim() !== '') return '1'
    return ''
  }

  const handleSubmit = () => {
    const formData = new FormData()
    // Dynamically append all agreementPending fields
    delete agreementPending.pending_chapters
    delete agreementPending.pending_agreement
    Object.entries(agreementPending).forEach(([key, value]) => {
      // Skip specific keys
      if (
        [
          'xml_file',
          'id',
          'is_new',
          'chapters',
          'pending_chapters',
          'parties',
          'sources',
          'is_sources_valid',
        ].includes(key)
      )
        return

      if (value !== null && value !== undefined) {
        let formattedValue = value

        if (key === 'date_signed' && value === '') {
          formattedValue = 'XXXX-XX-XX'
        }

        if ((key === 'notification' || key === 'type' || key === 'iso') && Array.isArray(value)) {
          formattedValue = value.join(';')
        }

        if (key === 'crossregional' || key === 'parties_escap') {
          formattedValue = String(value).toLowerCase()
        }
        formData.append(key, String(formattedValue)) // Convert value to string
      }
    })

    const sourcesString = serializeSources(pendingSources, agreementPending.sources)
    formData.append('sources', sourcesString)

    const sourceValidityString = serializeSourcesValidity(pendingSources, agreementPending.sources)
    formData.append('is_sources_valid', sourceValidityString)

    // BRA, CHL, EGY, ISR, KOR, MEX, PAR, PRY, PER, PHL, TUN, TUR, URY, SRB, EUN, BGD
    // West Asia; South America; Africa; Middle East; East Asia; North America; Europe
    // Europe; Africa & Middle East; Latin America & Caribbean; Asia Pacific

    confirmDialog(`Are you sure you want to submit this agreement?`).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      submitAgreement(axiosPrivate, idAgreement, formData)
        .then((res) => {
          if (res.status === 200) {
            ReactSwal.fire({
              title: 'Agreement reviewed',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              navigate('/new-rta')
            })
          }
        })
        .catch((err) => {
          console.error(err)
          ReactSwal.fire('Error', 'Failed to submit agreement', 'error')
        })
        .finally(() => {
          setIsLoadingButton(false)
        })
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        window.location.reload()
      }
    })
  }
  // End of Add or Edit

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'new-rta',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        Review Agreement
      </PageTitle>
      <div className='TradeDataAgreementDetailPage'>
        <div className='row'>
          {agreementOrigin.id != null ? (
            <div className='col-12'>
              <AgreementComparisonForm
                isLoadingAgreement={isLoadingAgreement}
                agreementOrigin={agreementOrigin}
                agreementPending={agreementPending}
                agreementType={agreementType}
                agreementStatus={agreementStatus}
                agreementNotification={agreementNotification}
                countries={countries}
                agreementComposition={agreementComposition}
                originalSources={originalSources}
                pendingSources={pendingSources}
                sourceColumns={sourceColumns}
                isDifferent={isDifferent}
                handleAgreementChange={handleAgreementChange}
                handlePartiesChange={handlePartiesChange}
                setAgreementPending={setAgreementPending}
                setShowSourceModal={setShowSourceModal}
                handleCancel={handleCancel}
                handleSubmit={handleSubmit}
                isLoadingButton={isLoadingButton}
                isFormValid={isFormValid}
              />
            </div>
          ) : (
            <div className='col-12'>
              <AgreementPendingForm
                isLoadingAgreement={isLoadingAgreement}
                agreementOrigin={agreementOrigin}
                agreementPending={agreementPending}
                agreementType={agreementType}
                agreementStatus={agreementStatus}
                agreementNotification={agreementNotification}
                countries={countries}
                agreementComposition={agreementComposition}
                pendingSources={pendingSources}
                sourceColumns={sourceColumns}
                isDifferent={isDifferent}
                handleAgreementChange={handleAgreementChange}
                handlePartiesChange={handlePartiesChange}
                setAgreementPending={setAgreementPending}
                setShowSourceModal={setShowSourceModal}
                handleCancel={handleCancel}
                handleSubmit={handleSubmit}
                isLoadingButton={isLoadingButton}
                isFormValid={isFormValid}
              />
            </div>
          )}
        </div>
      </div>
      <ModalAddSources
        show={showSourceModal}
        onHide={() => setShowSourceModal(false)}
        onSubmit={() => {
          handleAddSource()
          setShowSourceModal(false)
        }}
        alias={sourceAlias}
        link={sourceLink}
        onAliasChange={setSourceAlias}
        onLinkChange={setSourceLink}
      />
    </>
  )
}

export default TradeDataAgreementReviewPage
